<template>
	<div class="customer-service-wrapper">
		<!-- 聊天面板 -->
		<transition name="chat-panel">
			<div v-if="isOpen" class="chat-panel">
				<!-- 标题栏 -->
				<div class="chat-header">
					<div class="chat-header-left">
						<div class="chat-avatar-small">
							<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
								<path d="M3 18v-6a9 9 0 0 1 18 0v6" />
								<path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
							</svg>
						</div>
						<span class="chat-title">{{ t('customerService.在线客服') }}</span>
						<span
							:class="['connection-dot', wsConnected ? 'connected' : 'disconnected']"
							:title="wsConnected ? t('customerService.已连接') : t('customerService.未连接')"
						></span>
					</div>
					<button class="chat-close-btn" @click="isOpen = false" :title="t('customerService.关闭')">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
							<line x1="18" y1="6" x2="6" y2="18" />
							<line x1="6" y1="6" x2="18" y2="18" />
						</svg>
					</button>
				</div>

				<!-- 消息列表 -->
				<div class="chat-messages" ref="messagesContainer">
					<div v-for="(msg, index) in messages" :key="index" :class="['message-item', msg.type === 'user' ? 'message-user' : 'message-service']">
						<!-- 客服头像 -->
						<div v-if="msg.type === 'service'" class="message-avatar">
							<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
								<path d="M3 18v-6a9 9 0 0 1 18 0v6" />
								<path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
							</svg>
						</div>
						<!-- 消息内容 -->
						<div class="message-body">
							<div v-if="msg.text" class="message-bubble">
								{{ msg.text }}
							</div>
							<img v-if="msg.image" :src="msg.image" class="message-image" :alt="t('customerService.图片消息')" @click="previewImage = msg.image" />
						</div>
						<!-- 用户头像 -->
						<div v-if="msg.type === 'user'" class="message-avatar message-avatar-user">
							<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
								<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
								<circle cx="12" cy="7" r="4" />
							</svg>
						</div>
					</div>
				</div>

				<!-- 输入区域 -->
				<div class="chat-input-area">
					<textarea
						ref="inputRef"
						v-model="inputText"
						class="chat-input"
						:placeholder="t('customerService.请输入您的问题')"
						rows="1"
						@keydown="handleKeydown"
						@input="autoResize"
					></textarea>
					<!-- 隐藏的文件选择器 -->
					<input ref="fileInput" type="file" accept="image/*" style="display: none" @change="handleImageUpload" />
					<!-- 图片上传按钮 -->
					<button class="chat-image-btn" :disabled="uploading" @click="$refs.fileInput.click()" :title="t('customerService.发送图片')">
						<!-- loading 旋转图标 -->
						<svg v-if="uploading" class="spin-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
							<circle cx="12" cy="12" r="10" stroke-dasharray="32" stroke-linecap="round" />
						</svg>
						<!-- 图片图标 -->
						<svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
							<rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
							<circle cx="8.5" cy="8.5" r="1.5" />
							<polyline points="21 15 16 10 5 21" />
						</svg>
					</button>
					<button class="chat-send-btn" @click="sendMessage" :disabled="!inputText.trim()">
						<svg viewBox="0 0 24 24" fill="currentColor">
							<path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
						</svg>
					</button>
				</div>
			</div>
		</transition>

		<!-- 初始圆形按钮 -->
		<div :class="['service-button', { 'is-active': isOpen }]" @click="toggleChat">
			<!-- 客服图标 -->
			<svg class="service-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
				<path d="M3 18v-6a9 9 0 0 1 18 0v6" />
				<path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
			</svg>
		</div>

		<!-- 图片预览遮罩 -->
		<transition name="preview-fade">
			<div v-if="previewImage" class="image-preview-overlay" @click="previewImage = null">
				<img :src="previewImage" :alt="t('customerService.图片消息')" @click.stop />
				<button class="preview-close-btn" @click="previewImage = null" :title="t('customerService.关闭')">
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
						<line x1="18" y1="6" x2="6" y2="18" />
						<line x1="6" y1="6" x2="18" y2="18" />
					</svg>
				</button>
			</div>
		</transition>
	</div>
</template>

<script setup>
import { ref, reactive, nextTick, watch, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import WebSocketClient from '@/utils/websocket'
import { storageGet, storageSet } from '@/utils/storage'
import { isImageUrl } from '@/utils/common'

const { t } = useI18n()

const CHAT_SID_KEY = 'customer_service_session_id'

const isOpen = ref(false)
const inputText = ref('')
const messagesContainer = ref(null)
const inputRef = ref(null)
const wsConnected = ref(false)
const sessionId = ref('')
const uploading = ref(false)
const fileInput = ref(null)
const previewImage = ref(null)
const sessionClosed = ref(false) // 服务端主动关闭会话（如超时），此时不应再提示"重连"

// ── 消息列表（服务器为唯一数据源，不做本地持久化）────
const messages = reactive([])

// 发送队列：追踪最近发出的消息文本（最多 5 条），用于去重服务器回显
// 仅匹配 type === 'message' 的服务器回显，不影响客服回复
const sentTextQueue = []

// ── WebSocket 连接 ──────────────────────────────────
const WS_HOST = process.env.VUE_APP_WS_HOST

let ws = null
let sessionReady = false // 拿到 sessionId 后置 true，防止后续 connected 消息再次触发切换

function createWS(pathSuffix = '') {
	const url = `${WS_HOST}/chat/guest/${pathSuffix}`
	const client = new WebSocketClient(url, {
		debug: process.env.NODE_ENV === 'development',
		reconnectInterval: 3000,
		heartbeatInterval: 30000,
	})

	client.on('open', () => {
		wsConnected.value = true
	})

	client.on('message', (data) => {
		// 处理服务端主动关闭通知（如超时踢出）
		if (data.type === 'closed') {
			wsConnected.value = false
			sessionClosed.value = true // 标记会话已关闭，后续发送消息时不再提示"重连"
			client.close() // 阻止自动重连
			storageSet(CHAT_SID_KEY, null) // 清除失效的 sessionId
			const tip =
				data.reason === 'timeout' ? t('customerService.会话已超时，请刷新页面重新连接') : t('customerService.连接已关闭，请刷新页面重新连接')
			messages.push({ type: 'service', text: tip })
			scrollToBottom()
			return
		}

		// 第一步：获取 sessionId（仅首次 /new 触发）
		if (!sessionReady && data.type === 'connected' && data.sessionId) {
			sessionReady = true
			sessionId.value = data.sessionId
			storageSet(CHAT_SID_KEY, data.sessionId) // 持久化，刷新复用
			client.close()
			startChat(data.sessionId)
			return
		}

		// 普通文本消息
		const text = data.content || data.msg || data.text
		if (typeof text === 'string' && text.trim()) {
			// 去重：仅针对服务器回显的用户消息（type: 'message'），
			// 与发送队列比对，匹配则移除队列项并跳过本次添加
			if (data.type === 'message') {
				const dupIndex = sentTextQueue.indexOf(text)
				if (dupIndex !== -1) {
					sentTextQueue.splice(dupIndex, 1)
					return
				}
			}
			// type: 'message' = 客户消息, 'reply' = 客服回复
			const msgType = data.type === 'message' ? 'user' : 'service'
			// 服务端历史消息中，图片和文本的 type 都是 'message'，
			// 通过 content 是否为图片 URL 来区分渲染方式
			if (isImageUrl(text)) {
				messages.push({ type: msgType, image: text })
			} else {
				messages.push({ type: msgType, text })
			}
			scrollToBottom()
		}
	})

	client.on('close', () => {
		wsConnected.value = false
	})

	client.on('error', () => {
		wsConnected.value = false
	})

	return client
}

function startChat(id) {
	ws = createWS(id)
	ws.connect()
}

// 初始化：有旧 sessionId 直接复用，否则走 /new 获取
const savedSid = storageGet(CHAT_SID_KEY)
if (savedSid) {
	sessionReady = true
	sessionId.value = savedSid
	startChat(savedSid)
} else {
	ws = createWS('new')
	ws.connect()
}

onBeforeUnmount(() => {
	if (ws) ws.destroy()
})

function toggleChat() {
	isOpen.value = !isOpen.value
}

function handleKeydown(e) {
	if (e.key === 'Enter') {
		e.preventDefault()
		if (e.ctrlKey) {
			// Ctrl+Enter: 换行
			inputText.value += '\n'
			nextTick(() => autoResize())
		} else {
			// Enter: 发送
			sendMessage()
		}
	}
}

function autoResize() {
	const el = inputRef.value
	if (!el) return
	el.style.height = 'auto'
	el.style.height = Math.min(el.scrollHeight, 120) + 'px'
}

function sendMessage() {
	const text = inputText.value.trim()
	if (!text) return

	// 添加到消息列表
	messages.push({ type: 'user', text })

	// 加入发送队列用于后续去重，保持队列上限
	sentTextQueue.push(text)
	if (sentTextQueue.length > 5) {
		sentTextQueue.shift()
	}

	// 清空输入并重置高度
	inputText.value = ''
	nextTick(() => autoResize())

	// 通过 WebSocket 发送
	const sent =
		ws &&
		ws.send({
			type: 'message',
			content: text,
			sessionId: sessionId.value,
			timestamp: Date.now(),
		})

	if (!sent) {
		messages.push({
			type: 'service',
			text: sessionClosed.value ? t('customerService.会话已超时，请刷新页面重新连接') : t('customerService.连接已断开，正在尝试重连，请稍候'),
		})
		scrollToBottom()
	}

	scrollToBottom()
}

async function handleImageUpload(e) {
	const file = e.target.files[0]
	if (!file) return

	// 校验类型
	if (!file.type.startsWith('image/')) {
		ElMessage.warning(t('customerService.仅支持上传图片文件'))
		resetFileInput()
		return
	}

	// 校验大小（10MB）
	if (file.size > 10 * 1024 * 1024) {
		ElMessage.warning(t('customerService.图片大小不能超过 10MB'))
		resetFileInput()
		return
	}

	uploading.value = true
	try {
		const formData = new FormData()
		formData.append('folder', 'cover')
		formData.append('file', file)

		const res = await fetch(`${process.env.VUE_APP_BASE_API}/oss/uploadFileFin`, {
			method: 'POST',
			body: formData,
		})
		const data = await res.json()

		if (data.code === 200 && data.address) {
			// 添加图片消息
			messages.push({ type: 'user', image: data.address })

			// 通过 WebSocket 发送图片地址
			const sent =
				ws &&
				ws.send({
					type: 'image',
					content: data.address,
					sessionId: sessionId.value,
					timestamp: Date.now(),
				})

			if (!sent) {
				messages.push({
					type: 'service',
					text: sessionClosed.value ? t('customerService.会话已超时，请刷新页面重新连接') : t('customerService.连接已断开，正在尝试重连，请稍候'),
				})
			}
		} else {
			ElMessage.error(t('customerService.图片上传失败，请重试'))
		}
		scrollToBottom()
	} catch {
		ElMessage.error(t('customerService.图片上传失败，请检查网络'))
	} finally {
		uploading.value = false
		resetFileInput()
	}
}

function resetFileInput() {
	if (fileInput.value) {
		fileInput.value.value = ''
	}
}

function scrollToBottom() {
	nextTick(() => {
		const container = messagesContainer.value
		if (!container) return

		// 先滚动一次，确保文本消息已对齐底部
		container.scrollTop = container.scrollHeight

		// 检查是否有未加载完成的图片，等加载完再滚动一次
		const images = container.querySelectorAll('img.message-image')
		for (const img of images) {
			if (!img.complete) {
				img.addEventListener('load', () => {
					container.scrollTop = container.scrollHeight
				}, { once: true })
				img.addEventListener('error', () => {
					container.scrollTop = container.scrollHeight
				}, { once: true })
			}
		}
	})
}

// 打开聊天时滚动到底部
watch(isOpen, (val) => {
	if (val) {
		scrollToBottom()
	}
})
</script>

<style lang="less" scoped>
.customer-service-wrapper {
	position: fixed;
	bottom: 30px;
	right: 30px;
	z-index: 9998;
	font-family: 'Microsoft YaHei', 'PingFang SC', sans-serif;
}

/* ========== 初始圆形按钮 ========== */
.service-button {
	position: absolute;
	bottom: 0;
	right: 0;
	width: 60px;
	height: 60px;
	border-radius: 50%;
	background: linear-gradient(135deg, #4a90d9, #357abd);
	box-shadow: 0 4px 16px rgba(74, 144, 217, 0.4);
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: all 0.3s ease;
	z-index: 2;

	&:hover {
		transform: scale(1.08);
		box-shadow: 0 6px 24px rgba(74, 144, 217, 0.55);
	}

	&.is-active {
		opacity: 0;
		visibility: hidden;
		pointer-events: none;
		transform: scale(0.6);
	}

	.service-icon {
		width: 30px;
		height: 30px;
		color: #fff;
	}
}

/* ========== 聊天面板 ========== */
.chat-panel {
	position: absolute;
	bottom: 0;
	right: 0;
	width: 370px;
	height: 520px;
	background: #fff;
	border-radius: 12px;
	box-shadow: 0 8px 40px rgba(0, 0, 0, 0.15);
	display: flex;
	flex-direction: column;
	overflow: hidden;
	z-index: 1;
}

/* 面板过渡 */
.chat-panel-enter-active,
.chat-panel-leave-active {
	transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}
.chat-panel-enter-from,
.chat-panel-leave-to {
	opacity: 0;
	transform: translateY(20px) scale(0.95);
}

/* ========== 标题栏 ========== */
.chat-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 14px 18px;
	background: linear-gradient(135deg, #4a90d9, #357abd);
	color: #fff;

	.chat-header-left {
		display: flex;
		align-items: center;
		gap: 10px;
	}

	.chat-avatar-small {
		width: 32px;
		height: 32px;
		border-radius: 50%;
		background: rgba(255, 255, 255, 0.2);
		display: flex;
		align-items: center;
		justify-content: center;

		svg {
			width: 18px;
			height: 18px;
		}
	}

	.chat-title {
		font-size: 16px;
		font-weight: 600;
	}

	.connection-dot {
		width: 8px;
		height: 8px;
		border-radius: 50%;
		flex-shrink: 0;

		&.connected {
			background: #67c23a;
			box-shadow: 0 0 6px rgba(103, 194, 58, 0.6);
		}

		&.disconnected {
			background: #f56c6c;
			box-shadow: 0 0 6px rgba(245, 108, 108, 0.6);
		}
	}

	.chat-close-btn {
		width: 30px;
		height: 30px;
		border: none;
		background: rgba(255, 255, 255, 0.15);
		border-radius: 50%;
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: center;
		transition: background 0.2s;

		&:hover {
			background: rgba(255, 255, 255, 0.3);
		}

		svg {
			width: 16px;
			height: 16px;
			color: #fff;
		}
	}
}

/* ========== 消息区域 ========== */
.chat-messages {
	flex: 1;
	padding: 16px;
	overflow-y: auto;
	background: #f5f7fa;
	display: flex;
	flex-direction: column;
	gap: 16px;

	&::-webkit-scrollbar {
		width: 5px;
	}
	&::-webkit-scrollbar-thumb {
		background: #c0c4cc;
		border-radius: 3px;
	}
}

.message-item {
	display: flex;
	align-items: flex-start;
	gap: 10px;
	max-width: 85%;

	&.message-user {
		align-self: flex-end;
	}

	&.message-service {
		align-self: flex-start;
	}
}

.message-avatar {
	width: 34px;
	height: 34px;
	border-radius: 50%;
	background: #4a90d9;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;

	svg {
		width: 18px;
		height: 18px;
		color: #fff;
	}

	&.message-avatar-user {
		background: #67c23a;
	}
}

.message-body {
	display: flex;
	flex-direction: column;
	gap: 6px;
}

.message-bubble {
	padding: 10px 14px;
	border-radius: 12px;
	font-size: 14px;
	line-height: 1.6;
	word-break: break-word;

	.message-user & {
		background: #4a90d9;
		color: #fff;
		border-bottom-right-radius: 4px;
	}

	.message-service & {
		background: #fff;
		color: #333;
		border-bottom-left-radius: 4px;
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
	}
}

.message-image {
	max-width: 150px;
	border-radius: 10px;
	cursor: pointer;
	transition: transform 0.2s;

	&:hover {
		transform: scale(1.02);
	}
}

/* ========== 图片预览遮罩 ========== */
.image-preview-overlay {
	position: fixed;
	inset: 0;
	z-index: 9999;
	background: rgba(0, 0, 0, 0.8);
	display: flex;
	align-items: center;
	justify-content: center;

	img {
		max-width: 90vw;
		max-height: 90vh;
		border-radius: 8px;
		box-shadow: 0 4px 30px rgba(0, 0, 0, 0.3);
	}
}

.preview-close-btn {
	position: absolute;
	top: 24px;
	right: 24px;
	width: 40px;
	height: 40px;
	border: none;
	background: rgba(255, 255, 255, 0.15);
	border-radius: 50%;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: background 0.2s;

	&:hover {
		background: rgba(255, 255, 255, 0.3);
	}

	svg {
		width: 20px;
		height: 20px;
		color: #fff;
	}
}

/* 预览遮罩过渡 */
.preview-fade-enter-active,
.preview-fade-leave-active {
	transition: opacity 0.25s ease;
}
.preview-fade-enter-from,
.preview-fade-leave-to {
	opacity: 0;
}

/* ========== 输入区域 ========== */
.chat-input-area {
	display: flex;
	align-items: flex-end;
	gap: 10px;
	padding: 12px 16px;
	border-top: 1px solid #ebeef5;
	background: #fff;
}

.chat-input {
	flex: 1;
	min-height: 40px;
	max-height: 120px;
	border: 1px solid #dcdfe6;
	border-radius: 20px;
	padding: 10px 16px;
	font-size: 14px;
	font-family: inherit;
	line-height: 1.4;
	outline: none;
	resize: none;
	overflow-y: auto;
	scrollbar-width: none; /* Firefox */
	transition: border-color 0.2s;

	&::-webkit-scrollbar {
		display: none; /* Chrome/Safari/Edge */
	}

	&:focus {
		border-color: #4a90d9;
	}

	&::placeholder {
		color: #c0c4cc;
	}
}

.chat-image-btn {
	width: 40px;
	height: 40px;
	border-radius: 50%;
	border: 1px solid #dcdfe6;
	background: #fff;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: all 0.2s;
	flex-shrink: 0;
	color: #909399;

	&:hover:not(:disabled) {
		color: #4a90d9;
		border-color: #4a90d9;
	}

	&:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}

	svg {
		width: 20px;
		height: 20px;
	}
}

/* 上传中旋转动画 */
.spin-icon {
	animation: spin 1s linear infinite;
}

@keyframes spin {
	from {
		transform: rotate(0deg);
	}
	to {
		transform: rotate(360deg);
	}
}

.chat-send-btn {
	width: 40px;
	height: 40px;
	border-radius: 50%;
	border: none;
	background: linear-gradient(135deg, #4a90d9, #357abd);
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: all 0.2s;
	flex-shrink: 0;

	&:hover:not(:disabled) {
		transform: scale(1.05);
		box-shadow: 0 2px 10px rgba(74, 144, 217, 0.4);
	}

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	svg {
		width: 18px;
		height: 18px;
		color: #fff;
	}
}

/* ========== 响应式 ========== */
@media (max-width: 500px) {
	.customer-service-wrapper {
		bottom: 16px;
		right: 16px;
	}

	.chat-panel {
		width: calc(100vw - 32px);
		height: 460px;
		right: -8px;
		bottom: -8px;
	}

	.service-button {
		width: 52px;
		height: 52px;

		.service-icon {
			width: 26px;
			height: 26px;
		}
	}
}
</style>
